import { AccessTime, AccountBox, Close, Person, TableBar } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cancelOrderPaymentNew } from '../../api/order/cancelOrderPaymentNew';
import { Order as OrderType, OrderLog } from '../../apiTypes';
import { Currency } from '../../constants/currencies';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { Lang, useLocale } from '../../contexts/locale.context';
import Bill from '../../pages/POS/Bill';
import OrderItemsList from '../../pages/POS/OrderItemList/OrderItemList';
import PaidLabel from '../../QR/components/PaidLabel';
import { useCollection } from '../../utils/api';
import currency from '../../utils/currency';
import { dateLocaleMap } from '../../utils/dates';
import { getByCard, getCash } from '../../utils/paid';
import useMedia from '../../utils/useMedia';
import Confirmation from '../Confirmation';
import Loader from '../Loader';
import OrderItemStatus from '../OrderItemStatus';
import PrimaryButton from '../PrimaryButton';

const colorMap = {
  income: 'success.main',
  spending: 'error.main',
  transfer: 'warning.main',
  cancel: 'warning.main',
};

type Props = {
  order: DbDocument<OrderType>;
  onClose: () => void;
};

export const getLogComment = (log: OrderLog, lang: Lang, symbol?: Currency): string => {
  if (log.type === 'addToOrder') {
    return `${log.dishName} ${t.added_to_order[lang]}`;
  }
  if (log.type === 'removeFromOrder') {
    return `${log.dishName} ${t.removed_from_order[lang]}`;
  }
  if (log.type === 'changeItem') {
    return `${t.modified[lang]} ${log.dishName}`;
  }
  if (log.type === 'changeTable') {
    return `${t.modified_table_or_waiter[lang]}`;
  }
  if (log.type === 'open') {
    return `${t.order_created[lang]}`;
  }
  if (log.type === 'pay') {
    return `${t.paid[lang]}. ${
      log.state.paid?.byCard ? `${t.by_card[lang]}: ${currency(getByCard(log.state.paid), symbol)}. ` : ''
    }${log.state.paid?.cash ? `${t.cash[lang]}: ${currency(getCash(log.state.paid), symbol)}. ` : ''}`;
  }
  if (log.type === 'print') {
    return `${t.bill_printed[lang]}`;
  }
  if (log.type === 'split') {
    return `${t.order_split[lang]}`;
  }
  if (log.type === 'close') {
    return `${t.removed[lang]}`;
  }
  if (log.type === 'changeOrderStatus') {
    return `${t.order_status_changed[lang]}`;
  }
  if (log.type === 'changeDeliveryInfo') {
    return `${t.delivery_info_changed[lang]}`;
  }
  if (log.type === 'cancelPayment') {
    return `${t.payment_canceled[lang]}`;
  }
  if (log.type === 'changeClient') {
    return `${t.client_changed[lang]}`;
  }
  return '';
};

const OrderHistory = ({ order, onClose }: Props) => {
  const { lang } = useLocale();
  const { cafe, cafeSlug } = useAdmin();
  const { workersMap, dishesMap, worker, wallets, legalEntities, legalEntitiesMap } = useCafe();
  const { isSm, isMobile } = useMedia();

  const { data, isValidating } = useCollection<OrderLog>(`cafes/${cafeSlug}/orders/${order.id}/logs`, {
    orderBy: ['time', 'asc'],
  });

  const logs = [...(order.logs || []), ...(data || [])];
  const firstLog = logs[0];

  const [selectedLog, setLog] = useState<OrderLog | null>(logs[0] || null);

  const [confirmCancel, setConfirmCancel] = useState(false);

  const items = useMemo(
    () => (selectedLog ? sortBy(Object.values(selectedLog.state.items), 'addTime') : []),
    [selectedLog]
  );

  const cancelPayment = useCallback(() => {
    if (worker && wallets) {
      cancelOrderPaymentNew({ order, worker, cafeSlug, wallets });
      setConfirmCancel(false);
      onClose();
    }
  }, [cafeSlug, order, wallets, worker, onClose]);

  useEffect(() => {
    if (firstLog) {
      setLog(firstLog);
    }
  }, [firstLog]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {t.history[lang]}. {t.order[lang]} #{order.number}
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      {isValidating && <Loader open />}

      {!isValidating && (
        <DialogContent>
          <Grid container spacing={3}>
            {!isSm && (
              <Grid item xs={12} sm={5}>
                {selectedLog && (
                  <Box sx={{ height: window.innerHeight - (isSm ? 220 : 252), overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <AccessTime color="disabled" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: (t) => t.palette.text.secondary }}>
                          {format(selectedLog.time, 'HH:mm:ss')}
                        </Typography>
                      </Box>

                      {selectedLog.state.tableName && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <TableBar color="disabled" fontSize="small" />
                          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                            {selectedLog.state.tableName}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Person color="disabled" fontSize="small" />
                        <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                          {selectedLog.state.workerName}
                        </Typography>
                      </Box>

                      {selectedLog.state.clientName && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <AccountBox color="disabled" fontSize="small" />
                          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                            {selectedLog.state.clientName}
                          </Typography>
                        </Box>
                      )}

                      {selectedLog.state.status && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <OrderItemStatus status={selectedLog.state.status} size="small" color="disabled" />
                          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                            {selectedLog.state.status === 'complete'
                              ? t.delivered[lang]
                              : t[`status_${selectedLog.state.status}`][lang]}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {selectedLog.state.delivery && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ color: (t) => t.palette.text.secondary }}
                        >{`${t.customer[lang]}: ${selectedLog.state.delivery.name}`}</Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: (t) => t.palette.text.secondary }}
                        >{`${t.phone[lang]}: ${selectedLog.state.delivery.phoneNumber}`}</Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: (t) => t.palette.text.secondary }}
                        >{`${t.address[lang]}: ${selectedLog.state.delivery.address}`}</Typography>
                        {selectedLog.state.delivery.comment && (
                          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                            {selectedLog.state.delivery.comment}
                          </Typography>
                        )}
                      </>
                    )}

                    {Object.values(selectedLog.state.items).length === 0 && (
                      <Typography
                        sx={{ mt: 3 }}
                        variant="subtitle1"
                        align="center"
                        color={(t) => t.palette.text.disabled}
                      >
                        {t.no_items_in_order[lang]}
                      </Typography>
                    )}

                    <Box mt={2}>
                      {selectedLog.type !== 'print' && (
                        <OrderItemsList
                          preview
                          items={items}
                          canRemove={false}
                          canEditWight={false}
                          promotionsApplied={selectedLog.state.paid?.promotionsApplied || []}
                        />
                      )}
                      {selectedLog.type === 'print' && (
                        <Bill
                          cafe={cafe}
                          dishesMap={dishesMap}
                          discount={order.paid?.discount || 0}
                          service={order.paid?.service || 0}
                          items={selectedLog.state.items}
                          number={order.number}
                          created={order.created}
                          delivery={order.delivery}
                          promotionsApplied={order.paid?.promotionsApplied || []}
                          legalEntities={legalEntities}
                          legalEntitiesMap={legalEntitiesMap}
                        />
                      )}
                    </Box>

                    {selectedLog.state.paid && <PaidLabel paidTime={order.paidTime} />}
                  </Box>
                )}
              </Grid>
            )}

            <Grid item xs={12} sm={7}>
              <Box sx={{ height: window.innerHeight - (isSm ? 220 : 252), overflow: 'auto' }}>
                {logs.map((log) => (
                  <Box
                    key={log.time}
                    onClick={() => setLog(log)}
                    sx={{
                      p: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor:
                        !!selectedLog && log.time === selectedLog.time
                          ? (t) => `${t.palette.primary.main}20`
                          : undefined,
                      cursor: 'pointer',
                      ':hover': {
                        bgcolor: (t) => `${t.palette.secondary.main}20`,
                      },
                    }}
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={1}>
                        <Radio size="small" checked={!!selectedLog && log.time <= selectedLog?.time} />
                      </Grid>

                      <Grid item xs={2}>
                        <Typography lineHeight={1.3}>
                          {format(log.time, 'dd MMM', { locale: dateLocaleMap[lang] })}
                        </Typography>
                        <Typography lineHeight={1.3}>{format(log.time, 'HH:mm')}</Typography>
                      </Grid>

                      <Grid item xs={5}>
                        <Stack direction="row" alignItems="center" spacing={{ xs: 1 }}>
                          <Avatar src={workersMap[log.workerId]?.photoUrl} />
                          <span>{workersMap[log.workerId]?.name || log.workerName}</span>
                        </Stack>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography lineHeight={1.3} variant="body2">
                          {getLogComment(log, lang, cafe?.currency)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                {order.paid && worker?.credentials.to_cancel_payment && (
                  <Box sx={{ textAlign: 'right' }}>
                    <PrimaryButton color="error" onClick={() => setConfirmCancel(true)}>
                      {t.cancel_payment[lang]}
                    </PrimaryButton>
                  </Box>
                )}

                {confirmCancel && (
                  <Confirmation
                    open
                    onClose={() => setConfirmCancel(false)}
                    title={t.cancel_payment_confirmation[lang]}
                    text={
                      order.prroReceipts && Object.values(order.prroReceipts).length > 0 ? (
                        <Typography>{t.fiscalized_receipt_warning[lang]}</Typography>
                      ) : undefined
                    }
                    onSubmit={cancelPayment}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default OrderHistory;
